import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import type {ReactElement} from "react";
import {PageShell, type TabRoute} from "../layout/PageShell";
import AcademyStatisticsTab from "./academy/AcademyStatisticsTab";
import WeeklyPlannerReservationList from "./lists/WeeklyPlannerReservationList";
import WeeklyPlannerCalendar from "./weeklyPlanner/WeeklyPlannerCalendar";

/**
 * Stamped on everything the Forsthofgut Akademie configuration creates, including its sector.
 *
 * The statistics tab is the Akademie's, not the weekly planner's: a hotel's Wochenprogramm is the
 * same sector type and has no courses, no credits and no departments to count, so it would get a
 * tab that could only ever be empty. This tag is the Academy's own mark and the cheapest honest
 * answer to "is this sector the Akademie" that does not need a request to find out.
 */
const ACADEMY_BUILD_TAG = "academy_build";

interface Props {
    appName: string;
    icon: ReactElement;
    tabPath: (tab: string) => string;
    sector: Sector;
}

export default function WeeklyPlanner({appName, icon, tabPath, sector}: Props) {
    const {t} = useLocalize();

    const tabs: TabRoute[] = [
        {
            path: tabPath("calendar"),
            label: t("Calendar"),
            render: () => sector.id
                ? <WeeklyPlannerCalendar sector={sector}/>
                : null,
        },
        {
            path: tabPath("list"),
            label: t("List"),
            render: () => sector.id
                ? <WeeklyPlannerReservationList sectorId={sector.id}/>
                : null,
        },
    ];

    if (sector.id && sector.tags?.[ACADEMY_BUILD_TAG]) {
        tabs.push({
            path: tabPath("statistics"),
            label: t("Statistics"),
            render: () => <AcademyStatisticsTab sectorId={sector.id as string}/>,
        });
    }

    return (
        <PageShell
            icon={icon}
            title={appName}
            documentTitle={appName}
            tabs={tabs}
        />
    );
}
